import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@baseline/web/supabase";

interface AdminTag {
	id: string;
	name: string;
}

interface AdminEdge {
	id: string;
	title: string;
	relation: string;
	direction: string;
}

interface AdminLibraryItem {
	id: string;
	contentId: string | null;
	userId: string;
	userEmail: string;
	sourceType: string;
	itemType: string;
	title: string;
	url: string | null;
	domain: string | null;
	contentStatus: string | null;
	summary: string | null;
	skeleton: string | null;
	content: string | null;
	tokens: {
		summary: number;
		skeleton: number;
		content: number;
		transcript: number;
	};
	publishedAt: string | null;
	addedAt: string;
	addedVia: string;
	isArchived: boolean;
	isFavorite: boolean;
	tags: AdminTag[];
	edges: AdminEdge[];
}

interface AdminLibraryResponse {
	items: AdminLibraryItem[];
	total: number;
	limit: number;
	offset: number;
}

interface ContentDetail {
	id: string;
	canonical_url: string;
	title: string | null;
	markdown: string | null;
	content: string | null;
}

interface AdminSearch {
	type?: string;
	status?: string;
	user?: string;
	item?: string;
}

const ITEM_TYPES = [
	"",
	"youtube_video",
	"ai_thread",
	"website",
	"url",
	"document",
	"upload",
];
const CONTENT_STATUSES = ["", "pending", "processing", "completed", "failed"];
const PAGE_SIZE = 50;

const INVERSE_LABELS: Record<string, string> = {
	informs: "informed by",
	extends: "extended by",
	supersedes: "superseded by",
	synthesizes: "synthesized by",
	evolved_from: "evolved into",
	branches: "branched from",
};

export const Route = createFileRoute("/nerve")({
	validateSearch: (search: Record<string, unknown>): AdminSearch => ({
		type: typeof search.type === "string" ? search.type : undefined,
		status: typeof search.status === "string" ? search.status : undefined,
		user: typeof search.user === "string" ? search.user : undefined,
		item: typeof search.item === "string" ? search.item : undefined,
	}),
	component: AdminLibrary,
});

function AdminLibrary() {
	const { type, status, user, item } = Route.useSearch();
	const navigate = useNavigate();
	const [offset, setOffset] = useState(0);

	const itemType = type || "";
	const contentStatus = status || "";

	const { data, isLoading, error } = useQuery({
		queryKey: [
			"admin-library",
			offset,
			itemType,
			contentStatus,
			user,
			item,
		],
		queryFn: async (): Promise<AdminLibraryResponse> => {
			const body: Record<string, unknown> = {
				limit: PAGE_SIZE,
				offset,
			};
			if (itemType) body.item_type = itemType;
			if (contentStatus) body.content_status = contentStatus;
			if (user) body.user_email = user;
			if (item) body.item_id = item;

			const { data, error } = await supabase.functions.invoke(
				"admin-library",
				{ body },
			);
			if (error) throw new Error(error.message || "Failed to fetch");
			return data as AdminLibraryResponse;
		},
		staleTime: 30_000,
	});

	const items = data?.items ?? [];
	const total = data?.total ?? 0;
	const hasNext = offset + PAGE_SIZE < total;
	const hasPrev = offset > 0;

	function updateSearch(updates: Partial<AdminSearch>) {
		setOffset(0);
		navigate({
			to: "/nerve",
			search: (prev) => {
				const next = { ...prev, ...updates };
				// Remove empty values
				for (const key of Object.keys(next)) {
					if (!next[key as keyof AdminSearch])
						delete next[key as keyof AdminSearch];
				}
				return next;
			},
		});
	}

	if (!data && isLoading) {
		return (
			<p className="text-muted animate-pulse py-24 text-center text-sm">
				Loading...
			</p>
		);
	}

	if (!data && error) {
		return (
			<p className="text-muted py-24 text-center text-sm">
				{error.message}
			</p>
		);
	}

	return (
		<div className="mx-auto max-w-4xl px-6 py-8">
			<h1 className="mb-1 text-xl font-semibold">Admin: Library Items</h1>
			<p className="text-muted mb-6 text-sm">
				Cross-user view of all library items.
			</p>

			{/* Filters */}
			<div className="mb-6 flex flex-wrap items-center gap-4">
				<label className="text-sm">
					<span className="text-muted mr-1">Type:</span>
					<select
						value={itemType}
						onChange={(e) =>
							updateSearch({ type: e.target.value || undefined })
						}
						className="border-line bg-surface rounded border px-2 py-1 text-sm"
					>
						{ITEM_TYPES.map((t) => (
							<option key={t} value={t}>
								{t || "All"}
							</option>
						))}
					</select>
				</label>
				<label className="text-sm">
					<span className="text-muted mr-1">Status:</span>
					<select
						value={contentStatus}
						onChange={(e) =>
							updateSearch({
								status: e.target.value || undefined,
							})
						}
						className="border-line bg-surface rounded border px-2 py-1 text-sm"
					>
						{CONTENT_STATUSES.map((s) => (
							<option key={s} value={s}>
								{s || "All"}
							</option>
						))}
					</select>
				</label>
				{user && (
					<span className="border-line bg-surface flex items-center gap-1 rounded border px-2 py-1 text-xs">
						User: {user}
						<button
							onClick={() => updateSearch({ user: undefined })}
							className="text-muted hover:text-foreground ml-1"
						>
							x
						</button>
					</span>
				)}
				{item && (
					<span className="border-line bg-surface flex items-center gap-1 rounded border px-2 py-1 text-xs">
						Item: {item.slice(0, 8)}
						<button
							onClick={() => updateSearch({ item: undefined })}
							className="text-muted hover:text-foreground ml-1"
						>
							x
						</button>
					</span>
				)}
				<span className="text-muted text-sm">
					{total.toLocaleString()} items
				</span>
			</div>

			{error && (
				<div className="mb-4 rounded bg-red-900 px-4 py-2 text-sm text-red-200">
					{error.message}
				</div>
			)}

			{isLoading && (
				<p className="text-muted animate-pulse py-12 text-center text-sm">
					Loading...
				</p>
			)}

			{!isLoading && items.length === 0 && (
				<p className="text-muted py-12 text-center text-sm">
					No items found.
				</p>
			)}

			{/* Cards */}
			<div className="space-y-4">
				{items.map((i) => (
					<ItemCard
						key={i.id}
						item={i}
						onFilterUser={(email) =>
							updateSearch({ user: email, item: undefined })
						}
						onViewItem={(id) =>
							updateSearch({ item: id, user: undefined })
						}
					/>
				))}
			</div>

			{/* Pagination */}
			{total > PAGE_SIZE && (
				<div className="mt-6 flex items-center gap-4">
					<button
						disabled={!hasPrev}
						onClick={() =>
							setOffset(Math.max(0, offset - PAGE_SIZE))
						}
						className="text-muted hover:text-foreground text-sm disabled:opacity-30"
					>
						Previous
					</button>
					<span className="text-muted text-sm">
						{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of{" "}
						{total}
					</span>
					<button
						disabled={!hasNext}
						onClick={() => setOffset(offset + PAGE_SIZE)}
						className="text-muted hover:text-foreground text-sm disabled:opacity-30"
					>
						Next
					</button>
				</div>
			)}
		</div>
	);
}

function ItemCard({
	item,
	onFilterUser,
	onViewItem,
}: {
	item: AdminLibraryItem;
	onFilterUser: (email: string) => void;
	onViewItem: (id: string) => void;
}) {
	const [showContent, setShowContent] = useState(false);

	const { data: contentDetail, isLoading: contentLoading } = useQuery({
		queryKey: ["admin-content", item.contentId],
		queryFn: async (): Promise<ContentDetail | null> => {
			if (!item.contentId) return null;
			const { data, error } = await supabase.functions.invoke(
				"admin-library",
				{ body: { content_id: item.contentId } },
			);
			if (error)
				throw new Error(error.message || "Failed to fetch content");
			return (data as { content: ContentDetail }).content;
		},
		enabled: showContent && !!item.contentId,
		staleTime: 60_000,
	});

	return (
		<div className="border-line rounded-lg border">
			{/* Header */}
			<div className="bg-surface-raised border-line flex items-start justify-between gap-4 rounded-t-lg border-b px-4 py-3">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<h2 className="truncate text-sm font-medium">
							{item.title}
						</h2>
						<StatusBadge status={item.contentStatus} />
					</div>
					{item.url && (
						<a
							href={item.url}
							target="_blank"
							rel="noopener noreferrer"
							className="text-muted hover:text-accent block truncate text-xs underline"
						>
							{item.domain || item.url}
						</a>
					)}
				</div>
				<div className="text-muted flex shrink-0 items-center gap-3 text-xs">
					<button
						onClick={() => onFilterUser(item.userEmail)}
						className="hover:text-foreground underline"
					>
						{item.userEmail.split("@")[0]}
					</button>
					<span>{item.itemType}</span>
					<span>{formatDate(item.addedAt)}</span>
				</div>
			</div>

			{/* Body */}
			<div className="space-y-3 px-4 py-3">
				{/* Token counts */}
				<div className="text-muted flex gap-4 text-xs">
					<span>
						Summary:{" "}
						<span className="text-foreground">
							{formatTokens(item.tokens.summary)}
						</span>
					</span>
					<span>
						Skeleton:{" "}
						<span className="text-foreground">
							{formatTokens(item.tokens.skeleton)}
						</span>
					</span>
					<span>
						Content:{" "}
						<span className="text-foreground">
							{formatTokens(item.tokens.content)}
						</span>
					</span>
					{item.tokens.transcript > 0 && (
						<span>
							Transcript:{" "}
							<span className="text-foreground">
								{formatTokens(item.tokens.transcript)}
							</span>
						</span>
					)}
				</div>

				{/* Summary */}
				{item.summary && (
					<p className="text-muted text-sm whitespace-pre-wrap">
						{item.summary}
					</p>
				)}

				{/* Skeleton */}
				{item.skeleton && (
					<pre className="text-muted overflow-x-auto text-xs whitespace-pre-wrap">
						{item.skeleton}
					</pre>
				)}

				{/* Tags */}
				{item.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{item.tags.map((tag) => (
							<span
								key={tag.id}
								className="bg-surface-raised rounded px-1.5 py-0.5 text-xs"
							>
								{tag.name}
							</span>
						))}
					</div>
				)}

				{/* Edges / Relationships */}
				{item.edges?.length > 0 && (
					<div>
						<Label>Relationships</Label>
						<div className="space-y-0.5">
							{item.edges.map((edge, i) => (
								<div key={i} className="text-xs">
									<span className="text-muted">
										{edge.direction === "outgoing"
											? edge.relation
											: (INVERSE_LABELS[edge.relation] ??
												edge.relation)}
									</span>{" "}
									<button
										onClick={() => onViewItem(edge.id)}
										className="text-accent hover:text-fg-strong underline"
									>
										{edge.title}
									</button>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Full content */}
				{item.content ? (
					<details>
						<summary className="text-accent hover:text-fg-strong cursor-pointer text-xs">
							View full content
						</summary>
						<pre className="text-muted mt-2 text-xs whitespace-pre-wrap">
							{item.content}
						</pre>
					</details>
				) : item.contentId ? (
					<div>
						{!showContent ? (
							<button
								onClick={() => setShowContent(true)}
								className="text-accent hover:text-fg-strong text-xs underline"
							>
								Load full content
							</button>
						) : contentLoading ? (
							<p className="text-muted animate-pulse text-xs">
								Loading content...
							</p>
						) : contentDetail?.markdown ||
						  contentDetail?.content ? (
							<pre className="text-muted mt-2 text-xs whitespace-pre-wrap">
								{contentDetail.markdown ??
									contentDetail.content}
							</pre>
						) : (
							<p className="text-muted text-xs">
								No content available
							</p>
						)}
					</div>
				) : null}
			</div>

			{/* Footer meta */}
			<div className="text-muted border-line flex flex-wrap gap-3 border-t px-4 py-2 text-xs">
				<span>{item.id.slice(0, 8)}</span>
				<span>via {item.addedVia}</span>
				{item.publishedAt && (
					<span>published {formatDate(item.publishedAt)}</span>
				)}
				{item.isArchived && <span>archived</span>}
				{item.isFavorite && <span>favorite</span>}
			</div>
		</div>
	);
}

function Label({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-fg-ghost mb-0.5 text-xs font-medium tracking-wider uppercase">
			{children}
		</h3>
	);
}

function StatusBadge({ status }: { status: string | null }) {
	if (!status) return null;
	const colors: Record<string, string> = {
		completed: "text-green-400",
		processing: "text-yellow-400",
		pending: "text-blue-400",
		failed: "text-red-400",
	};
	return (
		<span className={`shrink-0 text-xs ${colors[status] || "text-muted"}`}>
			{status}
		</span>
	);
}

function formatTokens(n: number): string {
	if (n === 0) return "—";
	return n.toLocaleString();
}

function formatDate(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}
